import {
  Activity,
  ActivityFeedItemDefinition,
  BodyProps,
} from "@ofqual/portal-activity-feed";

import { applicationFieldLabels, fieldLabels } from "../fieldLabels";
import { DownloadLink } from "@ofqual/portal-components/components/DownloadLink";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import { differenceInDays } from "date-fns";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/selectors";
import { orgsService } from "../../services";
import { Organisation } from "../../domain/Organisation";
import { Chip, useTheme } from "@material-ui/core";
import {
  ActivityBody,
  ApplicationActivityBody,
  AttachmentBodyProps,
} from "./types";
import React from "react";

export const buildDefinitions = (
  setFocussedActivity: (activity: Activity) => void,
  organisation: Organisation,
  canRecordCompletion: boolean
) => {
  const orgUpdate: ActivityFeedItemDefinition<ActivityBody> = {
    activityType: 2,
    Body: ({ body }) => (
      <>
        <b>Updated:</b>{" "}
        {body.changes.map((item) => (
          <Chip
            size="small"
            style={{ margin: 2, display: "flex-grid" }}
            label={fieldLabels[item.field]}
            className="ChangeChip"
            key={item.field}
          />
        ))}
      </>
    ),
    options: [
      {
        label: "Show Changes",
        onClick: (activity) => setFocussedActivity(activity),
      },
    ],
  };

  const orgPendingUpdate: ActivityFeedItemDefinition<ActivityBody> = {
    activityType: 3,
    Body: ({ body }) => (
      <>
        <b>Proposed Changes:</b>{" "}
        {body.changes.map((item) => (
          <Chip
            size="small"
            label={fieldLabels[item.field]}
            className="ChangeChip"
            key={item.field}
          />
        ))}
      </>
    ),
    options: [
      {
        label: "Show Changes",
        onClick: (activity) => setFocussedActivity(activity),
      },
    ],
  };

  const orgUpdateApproval: ActivityFeedItemDefinition<ActivityBody> = {
    activityType: 4,
    Body: ({ body }) => (
      <>
        <b>Approved Changes:</b>{" "}
        {body.changes.map((item) => (
          <Chip
            size="small"
            style={{ margin: 2, display: "flex-grid" }}
            label={fieldLabels[item.field]}
            className="ChangeChip"
            key={item.field}
          />
        ))}
      </>
    ),
    options: [
      {
        label: "Show Changes",
        onClick: (activity) => setFocussedActivity(activity),
      },
    ],
  };

  const orgUpdateRejection: ActivityFeedItemDefinition<ActivityBody> = {
    activityType: 5,
    Body: ({ body }) => (
      <>
        <b>Rejected Changes:</b>{" "}
        {body.changes.map((item) => (
          <Chip
            size="small"
            style={{ margin: 2, display: "flex-grid" }}
            label={fieldLabels[item.field]}
            className="ChangeChip"
            key={item.field}
          />
        ))}
      </>
    ),
    options: [
      {
        label: "Show Changes",
        onClick: (activity) => setFocussedActivity(activity),
      },
    ],
  };

  const orgEventCompleted: ActivityFeedItemDefinition<AttachmentBodyProps> = {
    activityType: 22,
    Body: ({ body, activity }) => {
      const getValue = (field: keyof typeof fieldLabels) =>
        body.changes.find((item) => item.field === field)?.newValue;
      const attachedDocuments = () => body.documents;

      const referenceId = activity.id;
      const meetingDate = getValue("meetingDate");
      const engagementEventType = getValue("engagementEventType");
      const meansOfCommunicationType = getValue("meansOfCommunicationType");
      const notes = getValue("notes");
      const wasAttendedValue = getValue("wasAttended");
      const wasAttended =
        wasAttendedValue == null
          ? "No"
          : wasAttendedValue.toString() === "true"
          ? "Yes"
          : "No";

      const { token } = useSelector(authSelector);

      const buildGetBlob = (blobId: string) => () =>
        orgsService.downloadEngagementEventAttachment(
          organisation.id,
          referenceId,
          blobId,
          { token: token }
        );

      return (
        <>
          <div>
            <b>{fieldLabels.meetingDateShortLabel}</b>{" "}
            {formatUtcDate(meetingDate, standardDateFormats.DATE_TIME)}
          </div>
          <div>
            <b>{fieldLabels.engagementEventType}</b> {engagementEventType}
          </div>
          <div>
            <b>{fieldLabels.meansOfCommunicationType}</b>{" "}
            {meansOfCommunicationType}
          </div>
          <div>
            <b>{fieldLabels.wasAttended}</b> {wasAttended}
          </div>
          <div>
            <b>{fieldLabels.notes}</b>{" "}
            <div className="ActivityNote">{notes}</div>
          </div>
          <div>
            <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
              {attachedDocuments().map((document: any) => (
                <li key={document.blobId} style={{ listStyleType: "none" }}>
                  <DownloadLink
                    fileName={document.fileName}
                    getBlob={buildGetBlob(document.blobId)}
                  />
                </li>
              ))}
            </ul>
          </div>
        </>
      );
    },
    options: [],
  };

  const orgEvent: ActivityFeedItemDefinition<ActivityBody> = {
    activityType: 25,
    Body: ({ body }) => {
      const theme = useTheme();

      const getValue = (field: keyof typeof fieldLabels) =>
        body.changes.find((item) => item.field === field)?.newValue;

      const meetingDate = getValue("meetingDate");
      const engagementEventType = getValue("engagementEventType");
      const meansOfCommunicationType = getValue("meansOfCommunicationType");
      const overdueDays =
        (meetingDate && differenceInDays(new Date(), new Date(meetingDate))) ||
        0;

      return (
        <>
          <div>
            <b>{fieldLabels.meetingDateShortLabel}</b>{" "}
            {formatUtcDate(meetingDate, standardDateFormats.DATE_TIME)}
          </div>
          <div>
            <b>{fieldLabels.engagementEventType}</b> {engagementEventType}
          </div>
          <div>
            <b>{fieldLabels.meansOfCommunicationType}</b>{" "}
            {meansOfCommunicationType}
          </div>
          {overdueDays > 0 && (
            <div style={{ color: theme.palette.error.main }}>
              <b>Overdue by {overdueDays} day(s)</b>
            </div>
          )}
        </>
      );
    },
    options: canRecordCompletion
      ? [
          {
            label: "Record Completion",
            onClick: (activity) => setFocussedActivity(activity),
          },
        ]
      : [],
  };

  const orgApplicationClosed: ActivityFeedItemDefinition<{
    reason: string;
    notes: string;
  }> = {
    activityType: 23,
    Body: ({ body }) => (
      <>
        <p>
          <b>Reason:</b>{" "}
        </p>
        <div className="ActivityNote">{body.reason}</div>
        <p>
          <b>Note:</b>
        </p>
        <div className="ActivityNote">
          {" "}
          <ReactMarkdown>{body.notes}</ReactMarkdown>
        </div>
      </>
    ),
    options: [],
  };

  const orgApplicationReopen: ActivityFeedItemDefinition<{
    notes: string;
  }> = {
    activityType: 24,
    Body: ({ body }) => (
      <div className="ActivityNote">
        <b>Note:</b> <ReactMarkdown>{body.notes}</ReactMarkdown>
      </div>
    ),
    options: [],
  };

  const orgApplicationReleased: ActivityFeedItemDefinition<unknown> = {
    activityType: 26,
    options: [],
  };

  const messageFromOfqual: ActivityFeedItemDefinition<{ message: string }> = {
    activityType: 36,
    Body: ({ body }) => <>{<ReactMarkdown>{body.message}</ReactMarkdown>}</>,
    options: [],
  };

  const messageFromAo: ActivityFeedItemDefinition<{ message: string }> = {
    activityType: 37,
    Body: ({ body }) => <>{<ReactMarkdown>{body.message}</ReactMarkdown>}</>,
    options: [],
    align: "left",
  };

  const EnAttachmentBody: React.FC<{
    bodyProps: BodyProps<{
      note: string;
      documents: {
        blobId: string;
        fileName: string;
        contentType: string;
      }[];
    }>;
  }> = ({ bodyProps }) => {
    const { activity, body } = bodyProps;
    const { token } = useSelector(authSelector);

    const buildGetBlob = (blobId: string) => () =>
      orgsService.downloadAttachment(organisation!.id, activity.id, blobId, {
        token,
      });

    return (
      <>
        <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
          {body.documents.map((document) => (
            <li key={document.blobId} style={{ listStyleType: "none" }}>
              <DownloadLink
                fileName={document.fileName}
                getBlob={buildGetBlob(document.blobId)}
              />
            </li>
          ))}
        </ul>
        {body.note && (
          <div className="ActivityNote">
            <b>Note:</b> {body.note}
          </div>
        )}
      </>
    );
  };

  const enAttachmentOfqual: ActivityFeedItemDefinition<{
    note: string;
    documents: {
      blobId: string;
      fileName: string;
      contentType: string;
    }[];
  }> = {
    activityType: 38,
    Body: ({ body, activity }) => {
      return <EnAttachmentBody bodyProps={{ activity, body }} />;
    },
  };

  const enAttachmentAo: ActivityFeedItemDefinition<{
    note: string;
    documents: {
      blobId: string;
      fileName: string;
      contentType: string;
    }[];
  }> = {
    activityType: 39,
    Body: ({ body, activity }) => {
      return <EnAttachmentBody bodyProps={{ activity, body }} />;
    },
    align: "left",
  };

  const orgApplicationRejected: ActivityFeedItemDefinition<unknown> = {
    activityType: 28,
    options: [],
  };

  const reviewAttachment: ActivityFeedItemDefinition<{
    note: string;
    documents: {
      blobId: string;
      fileName: string;
      contentType: string;
    }[];
  }> = {
    activityType: 44,
    Body: ({ body }) => {
      return (
        <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
          {body.documents.map((document) => (
            <li key={document.blobId} style={{ listStyleType: "none" }}>
              <b>File name: </b>
              {document.fileName}
            </li>
          ))}
        </ul>
      );
    },
  };

  const panelAttachment: ActivityFeedItemDefinition<{
    note: string;
    documents: {
      blobId: string;
      fileName: string;
      contentType: string;
    }[];
  }> = {
    activityType: 45,
    Body: ({ body }) => {
      return (
        <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
          {body.documents.map((document) => (
            <li key={document.blobId} style={{ listStyleType: "none" }}>
              <b>File name: </b>
              {document.fileName}
            </li>
          ))}
        </ul>
      );
    },
  };

  const deletedReviewAttachment: ActivityFeedItemDefinition<{
    fileName?: string;
  }> = {
    activityType: 46,
    Body: ({ body }) => {
      return (
        <>
          <b>File name: </b>
          {body.fileName}
        </>
      );
    },
  };

  const deletedPanelAttachment: ActivityFeedItemDefinition<{
    fileName?: string;
  }> = {
    activityType: 47,
    Body: ({ body }) => {
      return (
        <>
          <b>File name: </b>
          {body.fileName}
        </>
      );
    },
  };

  const rejectApplication: ActivityFeedItemDefinition<{
    rejectReason?: string;
  }> = {
    activityType: 48,
    Body: ({ body }) => {
      return (
        <>
          <p>
            <b>Reason: </b>
          </p>
          <div className="ActivityNote">{body.rejectReason}</div>
        </>
      );
    },
  };

  const assignOrg: ActivityFeedItemDefinition<{
    rejectReason?: string;
  }> = {
    activityType: 51,
    Body: ({ body }) => {
      return (
        <>
          <div className="ActivityNote">{body}</div>
        </>
      );
    },
  };

  const approveApplication: ActivityFeedItemDefinition<{
    recognisedOn?: string;
  }> = {
    activityType: 55,
    Body: ({ body }) => {
      return (
        <>
          <b>Recognised On: </b>
          {body.recognisedOn}
        </>
      );
    },
  };

  const withdrawApplication: ActivityFeedItemDefinition<{
    rejectReason?: string;
  }> = {
    activityType: 56,
    options: [],
  };

  const applicationUpdate: ActivityFeedItemDefinition<ApplicationActivityBody> = {
    activityType: 49,
    Body: ({ body }) => (
      <>
        <b>Updated:</b>{" "}
        {body.changes.map((item) => (
          <Chip
            size="small"
            style={{ margin: 2, display: "flex-grid" }}
            label={applicationFieldLabels[item.field]}
            className="ChangeChip"
            key={item.field}
          />
        ))}
      </>
    ),
    options: [
      {
        label: "Show Changes",
        onClick: (activity) => setFocussedActivity(activity),
      },
    ],
  };

  return [
    orgUpdate,
    orgPendingUpdate,
    orgUpdateApproval,
    orgUpdateRejection,
    orgEventCompleted,
    orgEvent,
    orgApplicationClosed,
    orgApplicationReopen,
    orgApplicationReleased,
    messageFromOfqual,
    messageFromAo,
    enAttachmentOfqual,
    enAttachmentAo,
    reviewAttachment,
    panelAttachment,
    orgApplicationRejected,
    applicationUpdate,
    deletedReviewAttachment,
    deletedPanelAttachment,
    rejectApplication,
    approveApplication,
    withdrawApplication,
    assignOrg,
  ];
};
